import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Typography, Box, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Question from "../../components/Question";
import ImageUpload from "../../components/ImageUpload";
import ContinueButton from "../../components/ContinueButton";
import { continueButtonSx } from "../../lib/uiformatting";
import { setPhotos, setImagesUploaded, setSkippedPhotos } from "../../redux/userSlice";
import { MinorUserContext } from "../../context/UserNameProvider";
import { callBackendApiPostFormData, alertOps } from "../../lib/api";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";
import logDrtEvent from '../../lib/logging';

export default function Photos(props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isMinor, firstName } = useContext(MinorUserContext);
  const cases = useSelector((state) => state.user.photosCaseOptions);

  const [files, setFiles] = useState([]);
  const user = useSelector((state) => state.user);
  const uploadedPhotos = useSelector((state) => state.user.photos);
  const caseID = useSelector((state) => state.user.caseID);
  const patientID = useSelector((state) => state.user.patientID);
  const isZocdoc = useSelector((state) => state.user.zocDoc);
  const previousCaseID = useSelector((state) => state.user.previousCaseID);
  const isPhotosUploadFlow = useSelector((state) => state.user.photosUpload);
  const payMethod = useSelector((state) => state.user.payMethod);

  const buttonLabel = isPhotosUploadFlow ? 'Submit' : 'Continue';

  const [isUploading, setIsUploading] = useState(false);

  async function handleContinue() {
    if (files.length < 3) {
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("caseID", isPhotosUploadFlow ? previousCaseID : caseID);
      formData.append("patientID", patientID);
      files.forEach(file => {
        formData.append('photos', file);
      });

      if (isPhotosUploadFlow) {
        // there will be no "stage" for non-post-intake
        formData.append("stage", "post-intake");
      }

      logDrtEvent(`[new] Image upload started for patient: ${patientID} and case: ${caseID}`);

      const response = await callBackendApiPostFormData("/api/v1/upload-images", formData);
      if (!response.ok) {
        logDrtEvent(`Image upload failed with status: ${response.status}`);
        if (response.status === 404) {
          await alertOps(user, "Image upload failed", `Image upload failed with status: ${response.status}, due to missing caseID or patientID`, "Image upload");
          logDrtEvent(`Image upload failed with status: ${response.status}, due to missing caseID or patientID`);
          if (isZocdoc) {
            navigate("/error", { state: { zocdoc: true } });
          } else if (isPhotosUploadFlow) {
            navigate("/error", { state: { photos: true } });
          } else {
            navigate("/error");
          }
        }
        throw new Error("Image upload failed");
      }

      const data = await response.json();
      if (data.pre_signed_urls) {
        dispatch(setPhotos(data.pre_signed_urls));
        dispatch(setImagesUploaded(true));
        const nextPath = isPhotosUploadFlow ? '/photos-thank-you' : '/body-part';
        setPageAndDeferNavigate(dispatch, nextPath, navigate);
      }
    } catch (err) {
      logDrtEvent(`Error uploading images with error: ${err}`);
      await alertOps(user, "Image upload failed", `Image upload failed with error: ${err}`, "Image upload");
      if (isZocdoc) {
        navigate("/error", { state: { zocdoc: true } });
      } else {
        navigate("/error");
      }
    } finally {
      setIsUploading(false);
    }
  }

  const handleBack = () => {
    if (user.uberDoc) {
      setPageAndDeferNavigate(dispatch, '/allergies', navigate);
    } else if (user.photosUpload) {
      if (cases?.length <= 1) {
        setPageAndDeferNavigate(dispatch, '/photos-patients', () =>
          navigate('/photos-patients')
        );
      } else {
        setPageAndDeferNavigate(dispatch, '/photos-cases', () =>
          navigate('/photos-cases')
        );
      }
    } else if (user.payMethod === "self") {
      setPageAndDeferNavigate(dispatch, '/payment', navigate);
    } else {
      setPageAndDeferNavigate(dispatch, '/card-file', navigate);
    }
  };

  return (
    <Question
      onBack={handleBack}
      title={isPhotosUploadFlow ? "Upload photos for your provider." : "Thanks! Let's complete your profile."}
      subtitle={
        <>
          <Typography variant="body1" sx={{ mb: "0.5rem", mt: "1rem" }}>
            Your dermatologist has requested <strong>3 photos</strong> of the area of concern.
            <strong> One should be close-up (~5 inches) and one about 10 inches away.</strong>
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Accordion sx={{ maxWidth: '900px', width: '100%' }}>
              <AccordionSummary
                aria-controls="panel1a-content"
                id="panel1a-header"
                sx={{ pb: 0, pt: 0 }}
                expandIcon={<ExpandMoreIcon />}
              >
                <Typography>
                  Here are some tips for taking photos for the doctor
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, textAlign: 'left', pb: 0 }}>
                <ul style={{ paddingInlineStart: '20px' }}>
                  <li>Make sure the area is in focus.</li>
                  <li>Make sure the area is well-lit.</li>
                </ul>
              </AccordionDetails>
            </Accordion>
          </Box>
          <Typography variant="body1" sx={{ mt: "0.5rem" }}>
            {isMinor ? (firstName + "'s") : "Your"} photos are confidential and will only be seen by {isMinor ? (firstName + "'s") : "your"} provider. Please focus on <strong>one skin issue</strong>.
          </Typography>
        </>
      }
      input={
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: '100%',
            maxWidth: '900px',
            margin: '0 auto',
            gap: 2
          }}
        >
          <ImageUpload
            multiple={true}
            files={files}
            setFiles={setFiles}
            uploadedUrls={uploadedPhotos}
            sx={{ width: '100%' }}
          />
        </Box>
      }
      footer={
        <Box
          sx={{
            ...continueButtonSx,
            flexDirection: "column",
            alignItems: "center",
            width: '100%',
            maxWidth: '900px',
            margin: '0 auto',
            position: "sticky",
            bottom: 0,
          }}
        >
          <ContinueButton
            onClick={handleContinue}
            disabled={files.length < 3 || isUploading}
            title={isUploading ? "Uploading..." : buttonLabel}
            selected={files.length >= 3}
          />
          <Typography
            variant="body2"
            color={files.length >= 3 ? "text.primary" : "text.secondary"}
            sx={{
              fontWeight: 'bold',
              textAlign: "center",
              mt: 0.5,
              mb: 1.5
            }}
          >
            {files.length}/3 images uploaded
          </Typography>
          {!isPhotosUploadFlow && payMethod === "insurance" &&
            <Typography
              variant="body2"
              onClick={() => {
                dispatch(setSkippedPhotos('yes'));
                setPageAndDeferNavigate(dispatch, '/body-part', navigate);
              }}
              sx={{
                cursor: 'pointer',
                color: 'piction.highlight',
                '&:hover': {
                  color: 'piction.highlight',
                },
                fontWeight: 'bold',
              }}
            >
              Skip for now
            </Typography>
          }
        </Box>
      }
    />
  );
}